import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { Transaction, Category, TotalAmounts } from '@/interfaces/transaction';
import { groupAndSortTransactions } from '@/utils/groupTransactions';
import transactionsServices from '@/api/transactionsServices';
import { convertToUtc } from '@/utils/dateHelpers';

interface MetaData {
  totalPages: number;
  page: number;
  totalElements: number;
}
interface TransactionsState {
  transactions: { [key: string]: Transaction[] };
  currFilters: string;
  nextPageValue: { [key: string]: Transaction[] };
  selectedTransaction: Transaction;
  metaData: { [key: string]: MetaData };
  initialize: { [key: string]: boolean };
  loading: boolean;
  error: string;
  groupedTransactions: Record<string, Transaction[]>;
  filters: string;
  categories: Category[];
  totalAmounts: TotalAmounts;
}

const initialState: TransactionsState = {
  transactions: {},
  currFilters: '',
  nextPageValue: {},
  selectedTransaction: {} as Transaction,
  metaData: {},
  initialize: {},
  loading: false,
  error: '',
  groupedTransactions: {},
  filters: '',
  categories: [],
  totalAmounts: {} as TotalAmounts,
};

export const fetchTransactionsCategories = createAsyncThunk('transactionsCetagories/fetch', async () => {
  return transactionsServices.loadCategories();
});

export const fetchTransactions = createAsyncThunk<
  any,
  { filters?: any },
  { state: RootState }
>('transactions/fetch', async ({ filters = {} }, { getState }) => {
  const {
    transactions: { metaData, currFilters },
  } = getState();
  const tmpFilters = {
    ...filters,
    startDate: convertToUtc(filters.startDate),
    endDate: convertToUtc(filters.endDate),
  };

  const page = metaData[currFilters].page;
  return transactionsServices.loadTransactions({ page, ...tmpFilters });
});

export const fetchTotalAmounts = createAsyncThunk('transactionsAmount/fetch', async ({ filters = {} }: { filters?: any }) => {
  const tmpFilters = {
    ...filters,
    startDate: convertToUtc(filters.startDate),
    endDate: convertToUtc(filters.endDate),
  };

  return transactionsServices.loadTotalAmounts(tmpFilters);
});

export const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    group: state => {
      state.groupedTransactions = groupAndSortTransactions(state.transactions[state.currFilters]);
    },
    setSelectedTransaction: (state, action: PayloadAction<Transaction>) => {
      state.selectedTransaction = action.payload;
    },
    filterByRecipient: (state, action: PayloadAction<string>) => {
      // const filteredTransaction =
      //   state.transactions[state.currFilters].filter((tr: Transaction) => {
      //     return  tr.bankAccountName?.toLowerCase()
      //               .startsWith(action.payload.toLowerCase())
      //   })
      // state.groupedTransactions = groupAndSortTransactions(filteredTransaction)
    },
    setupPage: (state, action: PayloadAction<number>) => {
      state.metaData[state.currFilters].page = action.payload;
    },
    saveFilters: (state, action: PayloadAction<string>) => {
      state.currFilters = action.payload;
    },
    initializeRequest: (state, action: PayloadAction<string>) => {
      state.initialize[action.payload] = true;
      state.transactions[action.payload] = [];
      state.nextPageValue[action.payload] = [];
      state.currFilters = action.payload;
      state.metaData[action.payload] = { totalPages: 0, page: 0, totalElements: 0 };
    },
    refresh: state => {
      state.initialize = {};
      state.transactions = {};
      state.metaData = {};
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchTransactions.pending, state => {
      state.transactions[state.currFilters] = [...state.transactions[state.currFilters], ...state.nextPageValue[state.currFilters]];
      state.nextPageValue[state.currFilters] = [];
      state.loading = true;
    });

    builder.addCase(fetchTransactions.fulfilled, (state, action) => {
      const {
        payload: { data, totalPages, totalElements },
      } = action;
      if (state.initialize[state.currFilters]) {
        state.transactions[state.currFilters] = data.slice(0, 20);
        state.nextPageValue[state.currFilters] = data.slice(20, 40);
        state.initialize[state.currFilters] = false;
        const tmpTotalPages = totalElements > 20 ? 2 : totalPages;
        state.metaData[state.currFilters] = { totalPages: tmpTotalPages, page: 1, totalElements };
      } else {
        state.nextPageValue[state.currFilters] = data;
        state.metaData[state.currFilters] = { ...state.metaData[state.currFilters], totalPages, totalElements };
      }
      state.loading = false;
    });

    builder.addCase(fetchTransactions.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });

    builder.addCase(fetchTransactionsCategories.fulfilled, (state, action) => {
      state.categories = action.payload.data;
    });

    builder.addCase(fetchTotalAmounts.fulfilled, (state, action) => {
      const filters = action.meta.arg.filters;
      state.totalAmounts = { ...action.payload, accountId: filters.accountId };
    });
  },
});

export const { group, setupPage, refresh, saveFilters, filterByRecipient, setSelectedTransaction, initializeRequest } =
  transactionsSlice.actions;

export const selectTransactions = (state: RootState) => state.transactions;

export default transactionsSlice.reducer;
